import {
    useTable as useReactTable,
    useRowSelect,
    Row,
    Column,
    TableInstance,
    TableState
} from 'react-table';
import { Checkbox } from 'components';

interface UseTable<T extends object> {
    columns: ReadonlyArray<Column<T>>;
    data: T[];
    hasSelect?: boolean;
    disableSelect?: boolean;
    disableSelectAll?: boolean;
    hasFilter?: boolean;
    hasSort?: boolean;
    isSingleSelect?: boolean;
    initialState?: Partial<TableState<T>>;
}

export type ExtendedTable<T extends object> = TableInstance<T> & {
    hasSort: boolean;
};

export function useTable<T extends object>({
    columns,
    data,
    hasSelect = false,
    disableSelect = false,
    disableSelectAll = false,
    hasSort = false,
    initialState,
    isSingleSelect
}: UseTable<T>): ExtendedTable<T> {
    const plugins = [];

    if (hasSelect) {
        plugins.push(useRowSelect);
    }

    const table = useReactTable<T>(
        {
            columns,
            data,
            manualSortBy: true,
            manualFilters: true,
            initialState,
            stateReducer: (newState, action) => {
                if (isSingleSelect) {
                    if (action.type === 'toggleRowSelected') {
                        // @ts-ignore
                        newState.selectedRowIds = {
                            [action.id]: true
                        };
                    }
                }

                return newState;
            }
        },
        ...plugins,
        (hooks) => {
            hasSelect &&
                hooks.visibleColumns.push((columns) => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }) => {
                            const { checked, indeterminate, ...rest } =
                                getToggleAllRowsSelectedProps();
                            if (isSingleSelect || disableSelectAll) {
                                return null;
                            }

                            return (
                                <Checkbox
                                    isDisabled={disableSelect}
                                    isChecked={checked}
                                    isIndeterminate={indeterminate}
                                    {...rest}
                                />
                            );
                        },
                        Cell: ({ row }: { row: Row<T> }) => {
                            const props = row.getToggleRowSelectedProps();

                            return (
                                <Checkbox
                                    isDisabled={
                                        disableSelect ||
                                        // @ts-ignore
                                        (row.original?.default &&
                                            !isSingleSelect)
                                    }
                                    isChecked={props.checked}
                                    {...props}
                                />
                            );
                        },
                        disableSortBy: true
                    },
                    ...columns
                ]);
        }
    );

    return { ...table, hasSort };
}
